import type { Marker } from '@sembo-private/maps';

import { MarkerType } from '@apps/main/modules/maps/markerFormatters';

import type {
  DestinationStaticInfo,
  Image,
  IngredientId,
  OverviewDestinationStaticInfo,
} from '@libs/trip-planner-interfaces';

import type {
  ActivityMapCardData,
  ActivityType,
  HighlightedActivity,
} from '../../models';

const ACTIVITY_SELECTION_MARKERS = [
  MarkerType.Activity,
  MarkerType.Golf,
  MarkerType.Destination,
] as string[];

export function getActivitySelectionMarkers(
  markers: Marker[],
  highlightedActivity: HighlightedActivity | undefined
): Marker[] {
  return markers
    .filter(
      (marker) =>
        marker.useContentFormatter &&
        ACTIVITY_SELECTION_MARKERS.includes(marker.useContentFormatter)
    )
    .map((marker) => {
      if (
        highlightedActivity &&
        marker.key.includes(highlightedActivity?.activityCode)
      ) {
        const baseCss = marker.cssClass ?? '';
        const cssClass = `${baseCss} highlighted`;
        // NOTE: Change of key is required to force redraw in frontend libs
        return {
          ...marker,
          cssClass,
          key: `${marker.key} highlighted`,
          displayPriority: 4,
        };
      }
      return marker;
    });
}

export function getMarkerFormatters(): Record<
  MarkerType.Activity | MarkerType.Golf | MarkerType.Destination,
  (marker: Marker) => HTMLDivElement
> {
  const factoryFn = (marker: Marker): HTMLDivElement => {
    const div = document.createElement('div');

    div.className = `activity-selection-marker ${marker.cssClass || ''}`;

    div.innerHTML = `
      <div class="activity-selection-map-marker-label">
        <span>${marker.data?.formattedPrice}</span>
      </div>
    `;
    return div;
  };
  const accommodationFactoryFn = (marker: Marker): HTMLDivElement => {
    const div = document.createElement('div');
    div.className = `trip-summary-accommodation-marker ${
      marker.cssClass || ''
    }`;
    div.innerHTML = `<img src="https://images.sembo.travel/trip-planner/Bed.png">`;
    return div;
  };
  return {
    [MarkerType.Activity]: factoryFn,
    [MarkerType.Golf]: factoryFn,
    [MarkerType.Destination]: accommodationFactoryFn,
  };
}

export function getActivityMapCardData(
  activityMapCard: HighlightedActivity | undefined,
  getStaticInfo: (
    activityCode: string,
    ingredientId: IngredientId
  ) => DestinationStaticInfo | OverviewDestinationStaticInfo | undefined,
  getImage: (
    image: Image,
    activityCode?: string,
    width?: number,
    height?: number
  ) => string
): ActivityMapCardData | undefined {
  if (!activityMapCard) {
    return;
  }

  const { ingredientId, activityCode } = activityMapCard;
  const staticInfo = getStaticInfo(activityCode, ingredientId);

  if (!staticInfo || !staticInfo.images || !staticInfo.images[0]) {
    return;
  }

  return {
    ingredientId: ingredientId,
    activityCode: activityCode,
    activityName: staticInfo.name,
    starsRating: staticInfo.semboSunRating,
    imageUrl: getImage(staticInfo.images[0]),
    activityType: staticInfo.typeOfHotel as ActivityType,
  };
}

export function getHighlightedActivity(
  activityCode: string | undefined,
  ingredientId: IngredientId | undefined
): HighlightedActivity | undefined {
  if (activityCode && ingredientId) {
    return {
      activityCode,
      ingredientId,
      showActivityMapCard: false,
    };
  } else {
    return undefined;
  }
}
